import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./motionPrimitives";

const METRICS = [
    { value: "+340%", label: "average organic traffic lift" },
    { value: "4.8x", label: "average paid ad ROAS" },
    { value: "12,500+", label: "qualified leads generated" },
    { value: "90 days", label: "to first measurable lift" },
];

const CASES = [
    {
        id: "case-study-card-fintech",
        tag: "Fintech SaaS",
        title: "From 40 to 210 monthly demo bookings in seven months",
        desc: "Technical SEO overhaul plus an AI lead-scoring layer that routed only sales-ready visitors to the calendar.",
        metric: "+425%",
        metricLabel: "demo bookings",
        image:
            "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1200&q=80",
    },
    {
        id: "case-study-card-healthcare",
        tag: "Healthcare",
        title: "Local SEO overhaul for a multi-location clinic group",
        desc: "Location pages, review strategy and paid search rebuilt around patient intent across every clinic.",
        metric: "3.1x",
        metricLabel: "appointment requests",
        image:
            "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80",
    },
    {
        id: "case-study-card-realestate",
        tag: "Real Estate",
        title: "AI-qualified outbound for a luxury brokerage",
        desc: "An AI agent answering inbound in seconds and qualifying buyers before an agent ever picks up the phone.",
        metric: "-38%",
        metricLabel: "cost per qualified lead",
        image:
            "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
    },
];

export default function Results() {
    return (
        <section id="results" className="bg-fog py-20 lg:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div data-testid="results-section-container">
                    <SectionHeading
                        eyebrow="Case studies"
                        title="Results that show up in revenue."
                    />

                    <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {METRICS.map((m, i) => (
                            <motion.div
                                key={m.label}
                                initial={{ opacity: 0, y: 28 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                                className="bg-navy rounded-2xl p-6 sm:p-8 border border-white/5"
                            >
                                <p className="font-mono text-3xl sm:text-4xl font-semibold text-gold">
                                    {m.value}
                                </p>
                                <p className="mt-2 text-xs sm:text-sm text-mist">{m.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {CASES.map((c, i) => (
                        <motion.article
                            key={c.id}
                            data-testid={c.id}
                            initial={{ opacity: 0, y: 36 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                            className="group bg-white rounded-3xl overflow-hidden border border-[#E2E8F0] hover:border-gold/70 hover:shadow-2xl hover:shadow-navy/10 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                        >
                            <div className="h-52 overflow-hidden">
                                <img
                                    src={c.image}
                                    alt={c.tag}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-7 flex flex-col flex-1">
                                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">
                                    {c.tag}
                                </span>
                                <h3 className="mt-3 font-display text-xl font-bold text-navy leading-snug">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-sm text-steel leading-relaxed flex-1">
                                    {c.desc}
                                </p>
                                <div className="mt-6 pt-5 border-t border-[#E2E8F0] flex items-center gap-3">
                                    <TrendingUp size={18} className="text-gold" />
                                    <p className="font-mono text-2xl font-semibold text-navy">
                                        {c.metric}
                                    </p>
                                    <p className="text-xs uppercase tracking-wider text-steel">
                                        {c.metricLabel}
                                    </p>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                <Reveal delay={0.1}>
                    <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-steel/70">
                        Representative engagement outcomes — placeholder metrics pending client approval
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
